<app-loading *ngIf="contentReady"></app-loading>

<section class="bb-page bb-clinic-calendar-page" [class.bb-is-en]="!isAr" [dir]="isAr ? 'rtl' : 'ltr'">
  <div class="bb-page-shell">

    <!-- Header & Actions -->
    <header class="bb-page-header">
      <div class="bb-page-title-block">
        @if (!isSubAdmin) {
        <span class="bb-badge bb-badge-soft-blue bb-mb-8">
          {{ labels.totalGoods }}:
          <strong>{{ formatMoney(warehouseTotalValue) }}</strong>
          {{ labels.currency }}
        </span>
        }

        <h2 class="bb-page-title">{{ getPageTitle() }}</h2>
        <p class="bb-page-subtitle">{{ getPageSubtitle() }}</p>
      </div>

      <div class="bb-header-actions">
        <button type="button" class="bb-btn bb-btn-primary" (click)="openQuickActionModal('order')">
          {{ labels.newOrder }}
        </button>

        <button type="button" class="bb-btn bb-btn-primary" (click)="openQuickActionModal('booking')">
          {{ labels.newBooking }}
        </button>

        @if (!isSubAdmin) {
        <button type="button" class="bb-btn bb-btn-secondary" (click)="openQuickActionModal('shifts')">
          {{ labels.supply }}
        </button>

        <button type="button" class="bb-btn bb-btn-secondary" (click)="openQuickActionModal('accounts')">
          {{ labels.accounts }}
        </button>
        }
      </div>
    </header>

    @if (canShowStockSearch()) {
      <div class="bb-card bb-calendar-stock-panel">
        <form class="bb-calendar-stock-search" (ngSubmit)="searchStock()">
          <input
            class="bb-input"
            type="text"
            name="stockSearchKey"
            [(ngModel)]="stockSearchKey"
            [placeholder]="labels.productOrServiceSearchPlaceholder"
            (keyup.enter)="searchStock()"
          />

          <button type="submit" class="bb-btn bb-btn-primary" [disabled]="stockSearchLoading">
            @if (stockSearchLoading) {
              {{ labels.loadingAction }}
            } @else {
              {{ 'Search' | transloco }}
            }
          </button>

          @if (stockSearchTouched) {
            <button type="button" class="bb-btn bb-btn-secondary" (click)="clearStockSearch()">
              {{ 'all data' | transloco }}
            </button>
          }
        </form>

        @if (stockSearchTouched && !stockSearchLoading) {
          @if (stockSearchResults.length) {
            <div class="bb-calendar-stock-results">
              @for (stock of stockSearchResults; track getStockCartId(stock) || $index) {
                <div class="bb-card bb-calendar-stock-card">
                  <span class="bb-calendar-stock-img">
                    <img [src]="getStockImage(stock)" [alt]="getStockDisplayName(stock)" />
                  </span>

                  <div class="bb-text-stack">
                    <strong class="bb-text-navy bb-font-bold">{{ getStockDisplayName(stock) }}</strong>
                    @if (stock.barcode) {
                      <span class="bb-text-muted bb-font-semibold">{{ stock.barcode }}</span>
                    }
                  </div>

                  <div class="bb-calendar-stock-meta">
                    <span class="bb-badge" [ngClass]="getCalendarItemTypeBadgeClass(stock)">
                      {{ getCalendarItemTypeLabel(stock) }}
                    </span>

                    @if (getCalendarItemType(stock) === 'product') {
                    <span class="bb-badge bb-badge-soft-cyan">
                      {{ labels.quantityLabel }}: {{ formatNumber(getStockQuantity(stock)) }}
                    </span>
                    }

                    <span class="bb-badge bb-badge-soft-blue">
                      {{ labels.priceLabel }}: {{ formatMoney(getStockPrice(stock)) }} {{ labels.currency }}
                    </span>
                  </div>

                  <button
                    type="button"
                    class="bb-icon-btn action-btn cart"
                    [class.active]="isInCart(stock)"
                    (click)="addStockToCart(stock)"
                    [title]="isInCart(stock) ? ('remove from cart' | transloco) : ('add or remove from cart' | transloco)"
                  >
                    <i
                      class="fa-solid"
                      [class.fa-cart-plus]="!isInCart(stock)"
                      [class.fa-trash-can]="isInCart(stock)"
                    ></i>
                  </button>
                </div>
              }
            </div>
          } @else {
            <div class="bb-empty-state bb-text-center">
              <app-nodata></app-nodata>
            </div>
          }
        }

        @if (cartItemCount > 0) {
          <button type="button" class="bb-floating-cart bb-calendar-floating-cart" (click)="cartPopUpOpen()">
            <span class="bb-floating-cart-count">{{ cartItemCount }}</span>
            <i class="fa-solid fa-cart-shopping"></i>
            {{ 'Cart' | transloco }}
          </button>
        }
      </div>
    }

    <!-- Calendar Card -->
    <div class="bb-card bb-calendar-card">
      <div class="bb-calendar-top">
        <div class="bb-page-title-block">
          <h3 class="bb-calendar-title">{{ getMonthTitle() }}</h3>
          <span class="bb-page-subtitle">{{ labels.monthHint }}</span>
        </div>

        <div class="bb-calendar-summary">
          @if (!isSubAdmin) {
          <button type="button" class="bb-calendar-summary-item" (click)="openMonthlyPopup('shifts')">
            <span class="bb-badge bb-badge-success">
              <i class="fa-solid fa-clock"></i>
              <strong>{{ formatNumber(getCurrentMonthShiftsTotal()) }}</strong>
            </span>
            <span class="bb-calendar-summary-label">{{ 'clinicCalendarSummary.shifts' | transloco }}</span>
          </button>
          }

          <button
            type="button"
            class="bb-calendar-summary-item"
            [class.bb-alert-pulse]="isMonthlyBookingsAlerting()"
            (click)="openMonthlyPopup('bookings', $event)"
          >
            <span class="bb-badge bb-badge-soft-blue">
              <i class="fa-solid fa-calendar-check"></i>
              <strong>{{ formatNumber(getCurrentMonthBookingsTotal()) }}</strong>
            </span>
            <span class="bb-calendar-summary-label">{{ 'clinicCalendarSummary.bookings' | transloco }}</span>
          </button>

          <button type="button" class="bb-calendar-summary-item" (click)="openMonthlyPopup('orders')">
            <span class="bb-badge bb-badge-warning">
              <i class="fa-solid fa-receipt"></i>
              <strong>{{ formatNumber(getCurrentMonthOrdersTotal()) }}</strong>
            </span>
            <span class="bb-calendar-summary-label">{{ labels.orders }}</span>
          </button>

          <button type="button" class="bb-calendar-summary-item" (click)="openMonthlyPopup('vaccinations')">
            <span class="bb-badge bb-badge-danger">
              <i class="fa-solid fa-syringe"></i>
              <strong>{{ formatNumber(getCurrentMonthVaccinationsTotal()) }}</strong>
            </span>
            <span class="bb-calendar-summary-label">{{ 'clinicCalendarSummary.vaccinations' | transloco }}</span>
          </button>
        </div>
      </div>

      @if (loading) {
        <div class="bb-empty-state bb-text-center">
          {{ labels.loading }}
        </div>
      }

      @if (serverError && !loading) {
        <div class="bb-form-alert bb-text-center bb-m-16">
          {{ serverError }}
        </div>
      }

      @if (!loading && !serverError) {
        <div class="bb-calendar-board">
          <div class="bb-calendar-grid">
            @for (day of calendarDays; track trackByDay($index, day)) {
              <div
                class="bb-calendar-day"
                [class.bb-calendar-day-other]="!day.isCurrentMonth"
                [class.bb-calendar-day-today]="day.isToday"
                [class.bb-calendar-day-open]="day.is_open && day.shifts.length > 0"
                [class.bb-calendar-day-closed]="!day.is_open || day.shifts.length === 0"
              >
                <div class="bb-calendar-day-head">
                  <span class="bb-calendar-day-number">{{ formatNumber(day.dayNumber) }}</span>
                  <span class="bb-calendar-day-name">{{ day.dayLabel }}</span>

                  @if (day.isToday) {
                    <span class="bb-badge bb-badge-soft-blue">{{ labels.today }}</span>
                  }
                </div>

                <div class="bb-calendar-day-content">
                  @if (day.bookings.length > 0 || day.ordersCount > 0 || day.vaccinations.length > 0 || (!isSubAdmin && day.is_open && day.shifts.length > 0)) {
                    <div class="bb-calendar-day-counters">
                      @if (!isSubAdmin && day.is_open && day.shifts.length > 0) {
                        <button
                          type="button"
                          class="bb-badge bb-badge-success bb-calendar-counter-btn"
                          [title]="labels.shifts"
                          (click)="openShiftsPopup(day)"
                        >
                          <i class="fa-solid fa-clock"></i>
                          <span>{{ formatNumber(day.shifts.length) }}</span>
                        </button>
                      }

                      @if (day.bookings.length > 0) {
                        <button
                          type="button"
                          class="bb-badge bb-badge-soft-blue bb-calendar-counter-btn"
                          [class.bb-alert-pulse]="isCounterAlerting(day, 'bookings')"
                          [title]="labels.bookings"
                          (click)="openBookingsPopup(day, $event)"
                        >
                          <i class="fa-solid fa-calendar-check"></i>
                          <span>{{ formatNumber(day.bookings.length) }}</span>
                        </button>
                      }

                      @if (day.ordersCount > 0) {
                        <button
                          type="button"
                          class="bb-badge bb-badge-warning bb-calendar-counter-btn"
                          [title]="labels.orders"
                          (click)="openOrdersPopup(day)"
                        >
                          <i class="fa-solid fa-receipt"></i>
                          <span>{{ formatNumber(day.ordersCount) }}</span>
                        </button>
                      }

                      @if (day.vaccinations.length > 0) {
                        <button
                          type="button"
                          class="bb-badge bb-badge-danger bb-calendar-counter-btn"
                          [class.bb-alert-pulse]="isCounterAlerting(day, 'vaccinations')"
                          [title]="labels.vaccinations"
                          (click)="openVaccinationsPopup(day)"
                        >
                          <i class="fa-solid fa-syringe"></i>
                          <span>{{ formatNumber(day.vaccinations.length) }}</span>
                        </button>
                      }
                    </div>
                  }

                  @if (!day.is_open || day.shifts.length === 0) {
                    <div class="bb-calendar-closed-state">
                      {{ labels.closed }}
                    </div>
                  }
                </div>
              </div>
            }
          </div>

          <div class="bb-calendar-nav-footer">
            <button type="button" class="bb-btn bb-btn-secondary" (click)="goPreviousMonth()">
              {{ labels.previousMonth }}
            </button>

            <button type="button" class="bb-btn bb-btn-primary" (click)="goToday()">
              {{ labels.currentMonth }}
            </button>

            <button type="button" class="bb-btn bb-btn-secondary" (click)="goNextMonth()">
              {{ labels.nextMonth }}
            </button>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Day Popup -->
  @if (selectedDay && selectedPopupType) {
    <div class="bb-modal-backdrop" (click)="closeDayPopup()">
      <div class="bb-modal-card bb-calendar-modal-card" (click)="$event.stopPropagation()">
        <div class="bb-modal-header">
          <h4>
            @if (selectedPopupType === 'bookings') {
              {{ labels.bookings }}
            } @else if (selectedPopupType === 'shifts') {
              {{ labels.shifts }}
            } @else if (selectedPopupType === 'orders') {
              {{ labels.orders }}
            } @else {
              {{ labels.vaccinations }}
            }
          </h4>

          <button type="button" class="bb-modal-close" (click)="closeDayPopup()">&times;</button>
        </div>

        <p class="bb-page-subtitle bb-text-center bb-mb-16">{{ getSelectedDayTitle() }}</p>

        @if (selectedPopupType === 'bookings') {
          <div class="bb-calendar-popup-list">
            @for (booking of selectedDay.bookings; track booking.id) {
              <div
                class="bb-card bb-calendar-popup-item bb-calendar-popup-item-booking"
                role="button"
                tabindex="0"
                (click)="acknowledgeBookingAlert(booking, $event)"
                (keydown.enter)="acknowledgeBookingAlert(booking, $event)"
              >
                <div class="bb-calendar-popup-top">
                  <div class="bb-text-stack">
                    <strong class="bb-text-navy bb-font-bold">{{ booking.clientName }}</strong>
                    <span class="bb-text-muted bb-font-semibold">{{ booking.serviceName }}</span>

                    @if (booking.phone) {
                      <a class="bb-text-muted bb-font-semibold" [href]="'tel:' + booking.phone">{{ booking.phone }}</a>
                    }
                  </div>

                  <div class="bb-calendar-popup-meta">
                    <span class="bb-badge bb-badge-soft-blue">{{ formatBookingTime(booking) }}</span>
                    <small class="bb-badge booking-status" [ngClass]="getBookingStatusClass(booking.status)">
                      {{ getBookingStatusLabel(booking.status) }}
                    </small>
                  </div>
                </div>

                <div class="bb-calendar-actions-row" (click)="$event.stopPropagation()">
                  @if (!isFinalStatus(booking.status)) {
                    @if (booking.status !== 1) {
                      <button class="bb-btn bb-btn-sm bb-btn-secondary" type="button" (click)="setBookingStatus(booking, 1)">
                        {{ labels.approved }}
                      </button>
                    }

                    @if (booking.status !== 4) {
                      <button class="bb-btn bb-btn-sm bb-btn-secondary" type="button" (click)="setBookingStatus(booking, 4)">
                        {{ labels.completed }}
                      </button>
                    }

                    <button class="bb-btn bb-btn-sm bb-btn-ghost" type="button" (click)="openDeferredDatePopup(booking)">
                      @if (booking.status === 3) {
                        {{ labels.changeDeferredDate }}
                      } @else {
                        {{ labels.deferred }}
                      }
                    </button>

                    @if (booking.status !== 2) {
                      <button class="bb-btn bb-btn-sm bb-btn-danger" type="button" (click)="setBookingStatus(booking, 2)">
                        {{ labels.rejected }}
                      </button>
                    }
                  }

                  <button class="bb-btn bb-btn-sm bb-btn-danger" type="button" (click)="deleteBooking(booking)">
                    {{ labels.delete }}
                  </button>
                </div>
              </div>
            }
          </div>
        }

        @if (selectedPopupType === 'orders') {
          <div class="bb-calendar-summary-box">
            <span>{{ labels.totalInvoicesMoney }}:</span>
            <strong>{{ formatMoney(selectedDay.ordersTotalMoney) }} {{ labels.currency }}</strong>
          </div>

          <div class="bb-calendar-popup-list">
            @for (order of selectedDay.orders; track order.id) {
              <div
                class="bb-card bb-calendar-popup-item bb-calendar-popup-item-order"
                role="button"
                tabindex="0"
                (click)="openOrderInvoicePopup(order, $event)"
                (keydown.enter)="openOrderInvoicePopup(order, $event)"
              >
                <div class="bb-text-stack">
                  <span class="bb-text-muted bb-font-semibold">{{ labels.invoiceNumber }}</span>
                  <strong class="bb-text-navy bb-font-bold">#{{ formatNumber(order.id) }}</strong>
                  <span class="bb-text-muted bb-font-semibold">{{ order.clientName }}</span>

                  @if (order.phone) {
                    <a class="bb-text-muted bb-font-semibold" [href]="'tel:' + order.phone" (click)="$event.stopPropagation()">{{ order.phone }}</a>
                  }
                </div>

                <div class="bb-calendar-order-meta-grid">
                  <div>
                    <span class="bb-text-muted bb-font-semibold">{{ labels.invoiceAmount }}</span>
                    <strong class="bb-text-blue bb-font-bold">{{ formatMoney(order.total) }} {{ labels.currency }}</strong>
                  </div>

                  <div>
                    <span class="bb-text-muted bb-font-semibold">{{ labels.orderTime }}</span>
                    <strong class="bb-text-blue bb-font-bold">{{ formatOrderTime(order) }}</strong>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        @if (selectedPopupType === 'vaccinations') {
          <div class="bb-calendar-popup-list">
            @for (vaccination of selectedDay.vaccinations; track vaccination.id) {
              <a
                class="bb-card bb-calendar-popup-item bb-calendar-popup-item-vaccination"
                [routerLink]="vaccination.petId ? ['/petvaccinations'] : null"
                [queryParams]="vaccination.petId ? { petId: vaccination.petId } : null"
              >
                <div class="bb-text-stack">
                  <strong class="bb-text-navy bb-font-bold">{{ vaccination.vaccineName }}</strong>
                  <span class="bb-text-muted bb-font-semibold">{{ vaccination.petName }}</span>
                </div>

                <div class="bb-calendar-popup-meta bb-text-danger">
                  <i class="fa-solid fa-syringe"></i>
                </div>
              </a>
            }
          </div>
        }

        @if (selectedPopupType === 'shifts') {
          <div class="bb-calendar-popup-list">
            @for (shift of selectedDay.shifts; track trackByShift($index, shift)) {
              <div class="bb-card bb-calendar-popup-item bb-calendar-popup-item-shift">
                <div class="bb-calendar-popup-top">
                  <div class="bb-text-stack">
                    <strong class="bb-text-navy bb-font-bold">{{ labels.shiftTime }}</strong>
                    <span class="bb-text-muted bb-font-semibold">{{ formatShift(shift) }}</span>

                    @if (isEmergencyShift(shift)) {
                      <small class="bb-badge bb-badge-warning">{{ getEmergencyShiftLabel() }}</small>
                    }
                  </div>

                  <div class="bb-calendar-popup-meta">
                    <span class="bb-badge bb-badge-success">
                      <i class="fa-solid fa-users"></i>
                      {{ formatNumber(getShiftUsersCount(shift)) }} {{ labels.employeesCount }}
                    </span>
                  </div>
                </div>

                <div class="bb-calendar-section">
                  <div class="bb-flex bb-items-center bb-justify-between bb-gap-12 bb-wrap bb-mb-8">
                    <span class="bb-text-muted bb-font-bold">{{ labels.shiftEmployees }}</span>
                    <span class="bb-badge bb-badge-success">
                      {{ formatNumber(getShiftUsersCount(shift)) }} {{ labels.employeesCount }}
                    </span>
                  </div>

                  @if (getShiftUsersForView(shift).length) {
                    <div class="bb-shift-selected-users">
                      @for (user of getShiftUsersForView(shift); track trackByShiftUser($index, user)) {
                        <div class="bb-calendar-shift-user-wrap">
                          <button
                            type="button"
                            class="bb-shift-user-chip"
                            [class.bb-calendar-active-chip]="isShiftUserActionsOpen(shift, user)"
                            [class.bb-calendar-disabled-chip]="isShiftUserDisabled(user)"
                            [disabled]="isShiftUserDisabled(user)"
                            (click)="!isShiftUserDisabled(user) && toggleShiftUserActions(shift, user, $event)"
                          >
                            <i class="fa-solid fa-user"></i>
                            {{ getShiftUserName(user) }}

                            @if (isShiftUserDisabled(user)) {
                              <small>{{ 'Base' | transloco }}</small>
                            }
                          </button>

                          @if (isShiftUserActionsOpen(shift, user)) {
                            <div class="bb-flex bb-gap-8 bb-wrap bb-p-8 bb-bg-white bb-border bb-rounded-sm" (click)="$event.stopPropagation()">
                              <button
                                type="button"
                                class="bb-btn bb-btn-sm bb-btn-ghost"
                                routerLink="/employee"
                                [queryParams]="getEmployeeDetailsQueryParams(user)"
                              >
                                <i class="fa-solid fa-id-card"></i>
                                {{ labels.employeeDetails }}
                              </button>

                              <button
                                type="button"
                                class="bb-btn bb-btn-sm bb-btn-secondary"
                                routerLink="/employeescollecting"
                                [queryParams]="getEmployeeSalesQueryParams(user)"
                              >
                                <i class="fa-solid fa-chart-line"></i>
                                {{ labels.employeeSales }}
                              </button>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    <span class="bb-badge bb-badge-soft-cyan">{{ labels.noShiftEmployees }}</span>
                  }
                </div>

                <div class="bb-calendar-money-grid">
                  <div class="bb-card bb-p-12 bb-text-center">
                    <span class="bb-text-muted bb-font-bold">{{ labels.shiftSales }}</span>
                    <strong class="bb-block bb-text-blue bb-font-bold bb-mt-8">
                      {{ formatMoney(getShiftSalesTotal(selectedDay.date, shift)) }} {{ labels.currency }}
                    </strong>
                  </div>

                  <div class="bb-card bb-p-12 bb-text-center">
                    <span class="bb-text-muted bb-font-bold">{{ labels.shiftExpenses }}</span>
                    <strong class="bb-block bb-text-danger bb-font-bold bb-mt-8">
                      {{ formatMoney(getShiftExpensesTotal(selectedDay.date, shift)) }} {{ labels.currency }}
                    </strong>
                  </div>

                  <div class="bb-card bb-p-12 bb-text-center" [class.bb-calendar-negative-card]="getShiftProfitTotal(selectedDay.date, shift) < 0">
                    <span class="bb-text-muted bb-font-bold">{{ labels.shiftProfit }}</span>
                    <strong
                      class="bb-block bb-font-bold bb-mt-8"
                      [class.bb-text-success]="getShiftProfitTotal(selectedDay.date, shift) >= 0"
                      [class.bb-text-danger]="getShiftProfitTotal(selectedDay.date, shift) < 0"
                    >
                      {{ formatMoney(getShiftProfitTotal(selectedDay.date, shift)) }} {{ labels.currency }}
                    </strong>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  }

  <!-- Monthly Popup -->
  @if (selectedMonthlyPopupType) {
    <div class="bb-modal-backdrop" (click)="closeMonthlyPopup()">
      <div class="bb-modal-card bb-calendar-modal-card bb-calendar-modal-card-lg" (click)="$event.stopPropagation()">
        <div class="bb-modal-header">
          <h4>{{ getMonthlyPopupTitle() }}</h4>
          <button type="button" class="bb-modal-close" (click)="closeMonthlyPopup()">&times;</button>
        </div>

        <p class="bb-page-subtitle bb-text-center bb-mb-16">{{ getMonthTitle() }}</p>

        @if (selectedMonthlyPopupType === 'orders') {
          <div class="bb-calendar-summary-box">
            <span>{{ labels.totalInvoicesMoney }}:</span>
            <strong>{{ formatMoney(getCurrentMonthOrdersMoneyTotal()) }} {{ labels.currency }}</strong>
          </div>

          <div class="bb-calendar-popup-list bb-calendar-popup-list-lg">
            @for (order of getCurrentMonthOrders(); track order.id) {
              <div
                class="bb-card bb-calendar-popup-item bb-calendar-popup-item-order"
                role="button"
                tabindex="0"
                (click)="openOrderInvoicePopup(order, $event)"
                (keydown.enter)="openOrderInvoicePopup(order, $event)"
              >
                <div class="bb-text-stack">
                  <span class="bb-text-muted bb-font-semibold">{{ labels.invoiceNumber }}</span>
                  <strong class="bb-text-navy bb-font-bold">#{{ formatNumber(order.id) }}</strong>
                  <span class="bb-text-muted bb-font-semibold">{{ order.clientName }}</span>

                  @if (order.phone) {
                    <a class="bb-text-muted bb-font-semibold" [href]="'tel:' + order.phone" (click)="$event.stopPropagation()">{{ order.phone }}</a>
                  }
                </div>

                <div class="bb-calendar-order-meta-grid">
                  <div>
                    <span class="bb-text-muted bb-font-semibold">{{ labels.invoiceAmount }}</span>
                    <strong class="bb-text-blue bb-font-bold">{{ formatMoney(order.total) }} {{ labels.currency }}</strong>
                  </div>

                  <div>
                    <span class="bb-text-muted bb-font-semibold">{{ labels.invoiceDate }}</span>
                    <strong class="bb-text-blue bb-font-bold">{{ formatOrderDateTime(order) }}</strong>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        @if (selectedMonthlyPopupType === 'bookings') {
          <div class="bb-calendar-popup-list bb-calendar-popup-list-lg">
            @for (booking of getCurrentMonthBookings(); track booking.id) {
              <div
                class="bb-card bb-calendar-popup-item bb-calendar-popup-item-booking"
                role="button"
                tabindex="0"
                (click)="acknowledgeBookingAlert(booking, $event)"
                (keydown.enter)="acknowledgeBookingAlert(booking, $event)"
              >
                <div class="bb-calendar-popup-top">
                  <div class="bb-text-stack">
                    <strong class="bb-text-navy bb-font-bold">{{ booking.clientName }}</strong>
                    <span class="bb-text-muted bb-font-semibold">{{ booking.serviceName }}</span>

                    @if (booking.phone) {
                      <a class="bb-text-muted bb-font-semibold" [href]="'tel:' + booking.phone">{{ booking.phone }}</a>
                    }
                  </div>

                  <div class="bb-calendar-popup-meta">
                    <span class="bb-badge bb-badge-soft-blue">{{ formatDateTime(booking.date) }}</span>
                    <small class="bb-badge booking-status" [ngClass]="getBookingStatusClass(booking.status)">
                      {{ getBookingStatusLabel(booking.status) }}
                    </small>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        @if (selectedMonthlyPopupType === 'vaccinations') {
          <div class="bb-calendar-popup-list bb-calendar-popup-list-lg">
            @for (vaccination of getCurrentMonthVaccinations(); track vaccination.id) {
              <a
                class="bb-card bb-calendar-popup-item bb-calendar-popup-item-vaccination"
                [routerLink]="vaccination.petId ? ['/petvaccinations'] : null"
                [queryParams]="vaccination.petId ? { petId: vaccination.petId } : null"
              >
                <div class="bb-text-stack">
                  <strong class="bb-text-navy bb-font-bold">{{ vaccination.vaccineName }}</strong>
                  <span class="bb-text-muted bb-font-semibold">{{ vaccination.petName }}</span>
                </div>

                <div class="bb-calendar-popup-meta bb-text-danger">
                  <span class="bb-badge bb-badge-danger">{{ formatDateTime(vaccination.date) }}</span>
                  <i class="fa-solid fa-syringe"></i>
                </div>
              </a>
            }
          </div>
        }

        @if (selectedMonthlyPopupType === 'shifts') {
          <div class="bb-calendar-popup-list bb-calendar-popup-list-lg">
            @for (day of getCurrentMonthDaysWithShifts(); track trackByDay($index, day)) {
              <div class="bb-card bb-calendar-popup-item bb-calendar-popup-item-shift">
                <div class="bb-calendar-popup-top">
                  <div class="bb-text-stack">
                    <strong class="bb-text-navy bb-font-bold">{{ getSelectedDayTitleForDate(day.date) }}</strong>
                    <span class="bb-text-muted bb-font-semibold">{{ formatNumber(day.shifts.length) }} {{ labels.shifts }}</span>
                  </div>

                  <div class="bb-calendar-popup-meta bb-text-success">
                    <i class="fa-solid fa-clock"></i>
                  </div>
                </div>

                <div class="bb-calendar-monthly-shifts">
                  @for (shift of day.shifts; track trackByShift($index, shift)) {
                    <div class="bb-calendar-monthly-shift-row">
                      <strong>
                        {{ formatShift(shift) }}

                        @if (isEmergencyShift(shift)) {
                          <small class="bb-badge bb-badge-warning">{{ getEmergencyShiftLabel() }}</small>
                        }
                      </strong>

                      <span class="bb-badge bb-badge-success">
                        {{ formatNumber(getShiftUsersCount(shift)) }} {{ labels.employeesCount }}
                      </span>

                      @if (getShiftUsersForView(shift).length) {
                        <span class="bb-text-muted bb-font-semibold">{{ getShiftUserNames(shift).join(' - ') }}</span>
                      } @else {
                        <span class="bb-text-muted bb-font-semibold">{{ labels.noShiftEmployees }}</span>
                      }

                      <div class="bb-calendar-money-grid bb-calendar-money-grid-compact">
                        <span>{{ labels.shiftSales }}: {{ formatMoney(getShiftSalesTotal(day.date, shift)) }} {{ labels.currency }}</span>
                        <span>{{ labels.shiftExpenses }}: {{ formatMoney(getShiftExpensesTotal(day.date, shift)) }} {{ labels.currency }}</span>
                        <strong [class.bb-text-danger]="getShiftProfitTotal(day.date, shift) < 0">
                          {{ labels.shiftProfit }}: {{ formatMoney(getShiftProfitTotal(day.date, shift)) }} {{ labels.currency }}
                        </strong>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  }

  @if (cartPopUp) {
    <div class="bb-modal-backdrop popup" (click)="closeCartPopup()">
      <div class="bb-modal-card popup-content" (click)="$event.stopPropagation()">
        <div class="bb-modal-header">
          <h4>{{ 'Cart' | transloco }}</h4>
          <button type="button" class="bb-modal-close" (click)="closeCartPopup()">&times;</button>
        </div>

        <div class="bb-cart-list">
          @for (item of cartItems; track item.id + '-' + (item.cartItemType || 'product')) {
            <div class="bb-cart-item">
              <span>{{ getStockDisplayName(item) }}</span>
              <button class="bb-icon-btn bb-icon-btn-danger" type="button" (click)="removeFromCart(item)">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          }
        </div>

        <div class="bb-modal-actions">
          <button class="bb-btn bb-btn-primary" type="button" (click)="quickOrder()">
            {{ 'proceed to add order' | transloco }}
          </button>

          <button class="bb-btn bb-btn-secondary" type="button" (click)="closeCartPopup()">
            {{ labels.close }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Invoice Popup -->
  @if (selectedInvoiceOrder; as invoiceOrder) {
    <div class="bb-modal-backdrop bb-calendar-invoice-backdrop" (click)="closeOrderInvoicePopup()">
      <div class="bb-modal-card bb-calendar-invoice-card" (click)="$event.stopPropagation()">
        <div class="bb-modal-header">
          <div class="bb-text-stack bb-text-center bb-w-100">
            <span class="bb-text-muted bb-font-bold">{{ labels.invoice }}</span>
            <h4>#{{ formatNumber(invoiceOrder.id) }}</h4>
            <p class="bb-page-subtitle bb-m-0">{{ formatOrderDateTime(invoiceOrder) }}</p>
          </div>

          <button type="button" class="bb-modal-close" (click)="closeOrderInvoicePopup()">&times;</button>
        </div>

        <div class="bb-calendar-invoice-grid">
          <div class="bb-card bb-p-12">
            <span class="bb-text-muted bb-font-semibold">{{ labels.customer }}</span>
            <strong class="bb-block bb-text-navy bb-font-bold bb-mt-8">{{ invoiceOrder.clientName }}</strong>
          </div>

          @if (invoiceOrder.phone) {
            <div class="bb-card bb-p-12">
              <span class="bb-text-muted bb-font-semibold">{{ labels.phoneLabel }}</span>
              <strong class="bb-block bb-text-navy bb-font-bold bb-mt-8">{{ invoiceOrder.phone }}</strong>
            </div>
          }
        </div>

        <div class="bb-label bb-mt-16">{{ labels.invoiceItems }}</div>

        @if (getInvoiceItems(invoiceOrder).length) {
          <div class="bb-calendar-invoice-items">
            <div class="bb-calendar-invoice-items-head">
              <span>{{ labels.product }}</span>
              <span>{{ labels.quantityLabel }}</span>
              <span>{{ labels.priceLabel }}</span>
              <span>{{ labels.lineTotalLabel }}</span>
            </div>

            @for (item of getInvoiceItems(invoiceOrder); track item.name + '-' + $index) {
              <div class="bb-calendar-invoice-item-row">
                <strong>{{ item.name }}</strong>
                <span>{{ formatNumber(item.quantity) }}</span>
                <span>{{ formatMoney(item.price) }}</span>
                <span>{{ formatMoney(item.lineTotal) }}</span>
              </div>
            }
          </div>
        } @else {
          <div class="bb-empty-state bb-text-center">{{ labels.noInvoiceItems }}</div>
        }

        <div class="bb-calendar-invoice-totals">
          <div>
            <span>{{ labels.subtotalLabel }}</span>
            <strong>{{ formatMoney(getOrderSubtotal(invoiceOrder)) }} {{ labels.currency }}</strong>
          </div>

          @if (getOrderDiscount(invoiceOrder) > 0) {
            <div>
              <span>{{ labels.discountLabel }}</span>
              <strong>-{{ formatMoney(getOrderDiscount(invoiceOrder)) }} {{ labels.currency }}</strong>
            </div>
          }

          @if (getOrderDeliveryFees(invoiceOrder) > 0) {
            <div>
              <span>{{ labels.deliveryFeesLabel }}</span>
              <strong>{{ formatMoney(getOrderDeliveryFees(invoiceOrder)) }} {{ labels.currency }}</strong>
            </div>
          }

          @if (getOrderPaidAmount(invoiceOrder) > 0) {
            <div>
              <span>{{ labels.paidLabel }}</span>
              <strong>{{ formatMoney(getOrderPaidAmount(invoiceOrder)) }} {{ labels.currency }}</strong>
            </div>
          }

          @if (getOrderRemainingAmount(invoiceOrder) > 0) {
            <div>
              <span>{{ labels.remainingLabel }}</span>
              <strong>{{ formatMoney(getOrderRemainingAmount(invoiceOrder)) }} {{ labels.currency }}</strong>
            </div>
          }

          <div class="bb-calendar-invoice-grand-total">
            <span>{{ labels.totalInvoicesMoney }}</span>
            <strong>{{ formatMoney(invoiceOrder.total) }} {{ labels.currency }}</strong>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Deferred Date Popup -->
  @if (showDeferredPopup) {
    <div class="bb-modal-backdrop" (click)="closeDeferredPopup()">
      <div class="bb-modal-card bb-modal-sm" (click)="$event.stopPropagation()">
        <div class="bb-modal-header">
          <h4>{{ labels.changeDeferredDate }}</h4>

          <button
            type="button"
            class="bb-modal-close"
            (click)="closeDeferredPopup()"
            [disabled]="isUpdatingStatus"
          >
            &times;
          </button>
        </div>

        <div class="bb-field">
          <label class="bb-label">{{ labels.deferredDate }}</label>
          <input
            class="bb-input"
            type="datetime-local"
            [(ngModel)]="deferredBookingDate"
          />
        </div>

        <div class="bb-flex bb-gap-12 bb-mt-16">
          <button
            type="button"
            class="bb-btn bb-btn-secondary bb-w-100"
            (click)="closeDeferredPopup()"
            [disabled]="isUpdatingStatus"
          >
            {{ labels.close }}
          </button>

          <button
            type="button"
            class="bb-btn bb-btn-primary bb-w-100"
            (click)="submitDeferredDate()"
            [disabled]="isUpdatingStatus || !deferredBookingDate"
          >
            @if (isUpdatingStatus) {
              {{ labels.loadingAction }}
            } @else {
              {{ labels.submit }}
            }
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Quick Component Modal -->
  @if (selectedQuickActionModal) {
    <div class="bb-modal-backdrop" (click)="closeQuickActionModal()">
      <div class="bb-modal-card bb-modal-xl" (click)="$event.stopPropagation()">
        <div class="bb-modal-header">
          <h4>{{ getQuickActionModalTitle() }}</h4>

          <button
            type="button"
            class="bb-modal-close"
            (click)="closeQuickActionModal()"
          >
            &times;
          </button>
        </div>

        <div class="bb-component-modal-body">
          @if (selectedQuickActionModal === 'order') {
            <app-addorderstore></app-addorderstore>
          }

          @if (selectedQuickActionModal === 'booking') {
            <app-newbooking></app-newbooking>
          }

          @if (selectedQuickActionModal === 'shifts') {
            <app-clinic-emergency-shift></app-clinic-emergency-shift>
          }

          @if (selectedQuickActionModal === 'accounts') {
            <app-dailyexpensespage></app-dailyexpensespage>
          }
        </div>
      </div>
    </div>
  }
</section>
